import React, { useEffect, useRef, useState } from "react";
import { Typography, TextField, Alert, AlertTitle } from "@mui/material";
import GeneratingIndicator from "../AI/GeneratingIndicator";
import AISummarySection from "../AI/AISummarySection";
import ErrorState from "../AI/ErrorState";
import Signature from "./Signature";
import TechIssues from "./TechIssues";
import { useGetFeatureFlag } from "hooks/useGetFeatureFlag";
import useIsClientKaiserPrimary from "hooks/api/insurance/useIsClientKaiserPrimary";
import CaregiverSignature from "./CaregiverSignature";
import { recordProcessPresented } from "api/process";

const MAX_PRESENTATION_ATTEMPTS = 3;

const EditNoteDetails = (props) => {
  const {
    classes,
    note,
    responseNote,
    responseError,
    interventionError,
    templateText,
    techIssues,
    setError,
    closeTip,
    noteRef,
    responseNoteRef,
    onChange,
    locked,
    serviceType,
    clinicianUserId,
    onInsertAiSummary,
    miraState,
    generate,
    setAiProcessSucceeded,
    aiProcessSucceeded,
    onNoteAiSummaryChange,
    onResponseNoteAiSummaryChange,
    setRequestId,
    associatedSessionNotes,
    billableItem,
    processData,
    onRegenerate,
    clientId,
    selectedClientId,
    onMiraResultPresented,
  } = props;

  const [openNoteSummaryAccordion, setOpenNoteSummaryAccordion] = useState(true);
  const [openResponseSummaryAccordion, setOpenResponseSummaryAccordion] = useState(true);
  const handledResultRef = useRef(null);
  const presentationRequestRef = useRef({
    acknowledged: false,
    attempts: 0,
    key: null,
  });
  const [presentationRetryVersion, setPresentationRetryVersion] = useState(0);

  const { data: aiTranscriptFlag } = useGetFeatureFlag(
    clinicianUserId,
    "caregiver_training_ai_note"
  );
  // cpt codes approved for AI session note generation
  const approvedCodes = ["97153", "FI153"];
  // codes currently in pilot
  const approvedPilotCodes = ["FI156", "97156"];
  const serviceTypeCode = String(serviceType?.code || "");

  const { isKaiserPrimary } = useIsClientKaiserPrimary(selectedClientId || clientId);
  const isKaiserAssessmentNote = serviceTypeCode === "97151" && isKaiserPrimary;

  const isAiEligible =
    billableItem?.off_platform === false &&
    associatedSessionNotes?.length === 1 && // We do not currently have the functionality to parse transcripts with multiple cpt codes.
    (approvedCodes.includes(serviceTypeCode) ||
      (approvedPilotCodes.includes(serviceTypeCode) && aiTranscriptFlag));

  const isCurrentMiraProcess =
    miraState.processData?.cacheHashId === processData?.cacheHashId &&
    miraState.targetProcess === processData?.targetProcess;
  const hasCompleteMiraSummary =
    typeof miraState.response?.summary_client_response === "string" &&
    miraState.response.summary_client_response.trim().length > 0 &&
    typeof miraState.response?.summary_client_progress_data === "string" &&
    miraState.response.summary_client_progress_data.trim().length > 0;

  // Boolean flags to determine whether to render AI-related components
  const showGeneratingIndicator =
    miraState.processStatus === "in_progress" &&
    isCurrentMiraProcess &&
    isAiEligible &&
    !techIssues &&
    !aiProcessSucceeded &&
    !miraState.error;

  const showAISummary =
    miraState.processStatus === "ready" &&
    hasCompleteMiraSummary &&
    isCurrentMiraProcess &&
    !techIssues &&
    isAiEligible;

  const showAiError =
    isAiEligible &&
    (miraState.error ||
      (miraState.processStatus === "ready" &&
        Boolean(miraState.response) &&
        isCurrentMiraProcess &&
        !hasCompleteMiraSummary));

  //showAiSummary === true implies process has finished. Ensure both accordions open.
  useEffect(() => {
    if (!showAISummary) {
      return;
    }
    if (showAISummary) {
      const resultKey = JSON.stringify([
        miraState.invocationId || processData?.cacheHashId,
        miraState.generationAttemptId || null,
      ]);
      if (handledResultRef.current === resultKey) return;
      if (setRequestId && processData?.cacheHashId) {
        setRequestId(processData.cacheHashId);
      }

      // if there's a response populate the originalContent state in top level component for actionlog tracking
      if (miraState.response) {
        if (handledResultRef.current !== resultKey) {
          handledResultRef.current = resultKey;
          onMiraResultPresented?.({
            generationAttemptId: miraState.generationAttemptId,
            correlationReceipt: miraState.correlationReceipt,
            invocationId: miraState.invocationId,
            noteOriginalContent: miraState.response.summary_client_response,
            responseOriginalContent: miraState.response.summary_client_progress_data,
          });
        }
        onNoteAiSummaryChange({
          originalContent: miraState.response.summary_client_response,
        });
        onResponseNoteAiSummaryChange({
          originalContent: miraState.response.summary_client_progress_data,
        });
      }

      setOpenNoteSummaryAccordion(true);
      setOpenResponseSummaryAccordion(true);
      //Sets aiProcessSucceeded to true if summary is retrieved successfully. Prevents a redundant generate call if notes are toggled.
      if (!miraState.error) {
        setAiProcessSucceeded(true);
      }
    }
  }, [
    showAISummary,
    miraState.response,
    miraState.error,
    miraState.generationAttemptId,
    miraState.correlationReceipt,
    miraState.invocationId,
    processData?.cacheHashId,
    onMiraResultPresented,
    onNoteAiSummaryChange,
    onResponseNoteAiSummaryChange,
    setAiProcessSucceeded,
    setRequestId,
  ]);

  useEffect(() => {
    if (!showAISummary || !miraState.generationAttemptId || !miraState.invocationId) {
      return undefined;
    }

    const key = `${miraState.invocationId}:${miraState.generationAttemptId}`;
    if (presentationRequestRef.current.key !== key) {
      presentationRequestRef.current = {
        acknowledged: false,
        attempts: 0,
        key,
      };
    }

    const requestState = presentationRequestRef.current;
    if (requestState.acknowledged || requestState.attempts >= MAX_PRESENTATION_ATTEMPTS) {
      return undefined;
    }

    requestState.attempts += 1;
    let cancelled = false;
    let retryTimer;

    recordProcessPresented(
      miraState.invocationId,
      miraState.generationAttemptId,
      miraState.correlationReceipt
    )
      .then(() => {
        if (!cancelled && presentationRequestRef.current.key === key) {
          presentationRequestRef.current.acknowledged = true;
        }
      })
      .catch(() => {
        if (!cancelled && presentationRequestRef.current.key === key) {
          const delay = 1000 * 2 ** (requestState.attempts - 1);
          retryTimer = setTimeout(() => {
            setPresentationRetryVersion((version) => version + 1);
          }, delay);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [
    showAISummary,
    miraState.generationAttemptId,
    miraState.invocationId,
    presentationRetryVersion,
    miraState.correlationReceipt,
  ]);

  // Process keys this component has already auto-generated. A failed generation resets
  // miraState, so without this guard the effect below would request the same note again on
  // every failure. After the first automatic attempt, only the retry button re-runs it.
  const autoRequestedRef = useRef(new Set());

  useEffect(() => {
    // Skip if this exact process was already requested (e.g. by the regenerate button)
    const alreadyRequested =
      miraState.processData?.cacheHashId === processData?.cacheHashId &&
      miraState.targetProcess === processData?.targetProcess;
    const autoRequestKey = processData && `${processData.targetProcess}:${processData.cacheHashId}`;
    if (
      isAiEligible &&
      !aiProcessSucceeded &&
      processData &&
      !alreadyRequested &&
      !autoRequestedRef.current.has(autoRequestKey)
    ) {
      autoRequestedRef.current.add(autoRequestKey);
      generate(processData);
    }
  }, [
    isAiEligible,
    aiProcessSucceeded,
    processData,
    miraState?.processData?.cacheHashId,
    miraState?.targetProcess,
    generate,
  ]);

  const handleInsertSummary = (name, value) => {
    if (name === "note") {
      setOpenNoteSummaryAccordion(false);
    } else {
      setOpenResponseSummaryAccordion(false);
    }
    onInsertAiSummary(name, value);
  };

  const handleAccordionToggle = (name) => {
    if (name === "note") {
      setOpenNoteSummaryAccordion((prev) => !prev);
    } else {
      setOpenResponseSummaryAccordion((prev) => !prev);
    }
  };

  return (
    <>
      {showAiError && <ErrorState onRetry={onRegenerate} />}
      {showGeneratingIndicator && <GeneratingIndicator />}
      <div className={classes.noteDetailsContainer}>
        {techIssues ? (
          <TechIssues {...props} />
        ) : (
          <>
            {templateText.noteBlockTitle && (
              <div className={classes.noteDetail} ref={noteRef}>
                <div className={classes.noteDetailTitleContainer}>
                  <div className={classes.noteDetailTitle}>
                    <Typography component="p" variant="subtitle1">
                      {templateText.noteBlockTitle}
                    </Typography>
                  </div>
                  <Typography component="p" variant="body2Secondary">
                    {templateText.noteBlockInstruction}
                  </Typography>
                </div>
                {showAISummary && (
                  <AISummarySection
                    title="Summary of Client Response"
                    content={miraState.response.summary_client_response}
                    expanded={openNoteSummaryAccordion}
                    addSummary={(content) => handleInsertSummary("note", content)}
                    onAiSummaryChange={onNoteAiSummaryChange}
                    entityName={"Session Note"}
                    onToggle={() => handleAccordionToggle("note")}
                  />
                )}
                <TextField
                  fullWidth
                  multiline={true}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  color="primary"
                  minRows={1}
                  value={note}
                  onChange={onChange("note")}
                  disabled={locked}
                  size="medium"
                  error={!!interventionError}
                  placeholder={templateText.noteBlockPlaceholder}
                  onFocus={() => setError("interventionError", false)}
                  onBlur={() => {
                    closeTip("noteTipOpen");
                    setError("interventionError", !note || note.length === 0);
                  }}
                  helperText={
                    interventionError
                      ? "Type the Intervention inside the box for this session note"
                      : null
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      boxShadow: "none",
                    },
                  }}
                />
              </div>
            )}
            {templateText.responseBlockTitle && (
              <div className={classes.noteDetail} ref={responseNoteRef}>
                <div className={classes.noteDetailTitleContainer}>
                  <div className={classes.noteDetailTitle}>
                    <Typography component="p" variant="subtitle1">
                      {templateText.responseBlockTitle}
                    </Typography>
                  </div>
                  <Typography component="p" variant="body2Secondary">
                    {templateText.responseBlockInstruction}
                  </Typography>
                </div>
                {showAISummary && (
                  <AISummarySection
                    title="Summary of Client Response and Data"
                    content={miraState.response.summary_client_progress_data}
                    expanded={openResponseSummaryAccordion}
                    onAiSummaryChange={onResponseNoteAiSummaryChange}
                    entityName={"Session Note"}
                    addSummary={(content) => handleInsertSummary("responseNote", content)}
                    onToggle={() => handleAccordionToggle("responseNote")}
                  />
                )}
                <TextField
                  fullWidth
                  multiline={true}
                  minRows={1}
                  size="medium"
                  variant="standard"
                  color="primary"
                  InputProps={{ disableUnderline: true }}
                  value={responseNote}
                  error={!!responseError}
                  onChange={onChange("responseNote")}
                  placeholder={templateText.responseBlockPlaceholder}
                  disabled={locked}
                  onFocus={() => setError("responseError", false)}
                  onBlur={() => {
                    closeTip("responseTipOpen");
                    setError("responseError", !responseNote || responseNote.length === 0);
                  }}
                  helperText={
                    responseError ? "Type the Response inside the box for this session note" : null
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      boxShadow: "none",
                    },
                  }}
                />
                {isKaiserAssessmentNote && (
                  <Alert severity="info" sx={{ width: "100%", mt: 2 }}>
                    <AlertTitle sx={{ fontSize: 16 }}>
                      {"Shared with this client's insurer."}
                    </AlertTitle>
                    {
                      'The "Assessment items completed" section is included in the treatment plan submitted for this assessment.'
                    }
                  </Alert>
                )}
              </div>
            )}
            <Signature {...props} />
            <CaregiverSignature
              billableItem={props.billableItem}
              classes={classes}
              startDate={props.startDate}
              endDate={props.endDate}
              timezone={props.customerDetails?.timezone}
            />
          </>
        )}
      </div>
    </>
  );
};

export default EditNoteDetails;
